import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { IntercompanyReturnLink } from 'returnCenter-api';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import useOpenLinkedReturn from 'hooks/useOpenLinkedReturn';
import { returnSelectors } from 'store/reducers/returnSlice';
import { getStatsTranslations } from 'utils/helpers';

/**
 * The returns on the other side of an intercompany link: the intercompany returns a customer return
 * raised, or the customer return an intercompany return was raised from.
 */
const ConnectedReturnsCard = () => {
    const { t } = useTranslation();
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const { open, canOpen } = useOpenLinkedReturn();

    const links: IntercompanyReturnLink[] = returnSlip?.parentReturn
        ? [returnSlip.parentReturn]
        : returnSlip?.intercompanyReturns ?? [];

    if (links.length === 0) return null;

    return (
        <Flex column>
            <Text as="label" xBold fontSize={11} className="mb-1" padding={0} margin={0}>
                {returnSlip?.parentReturn
                    ? t('intercompany.customerReturn', 'Customer return')
                    : t('intercompany.intercompanyReturns', 'Intercompany returns')}
            </Text>
            <Wrapper column gap="0.5rem">
                {links.map((link) => (
                    <LinkItem key={link.returnId}>
                        <LinkButton
                            type="button"
                            disabled={!canOpen(link)}
                            title={
                                canOpen(link)
                                    ? undefined
                                    : t('intercompany.noAccessToCompany', 'You do not have access to this company')
                            }
                            onClick={() => open(link)}
                        >
                            <BolderText as="span">
                                #{link.returnId}
                                {link.supplyingCompany && (
                                    <StyledText thin as="span">{` (${link.supplyingCompany})`}</StyledText>
                                )}
                            </BolderText>
                        </LinkButton>
                        <StyledText thin as="span">
                            {getStatsTranslations(t, link.status)}
                        </StyledText>
                    </LinkItem>
                ))}
            </Wrapper>
        </Flex>
    );
};

export default ConnectedReturnsCard;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 1rem;
    border-radius: var(--radius);
`;

const BolderText = styled(Text)`
    font-size: 12px;
    font-weight: 600;
`;

const StyledText = styled(Text)`
    font-size: 12px;
    font-weight: 400;
`;

const LinkItem = styled(Flex)`
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight ?? '#eee'};
    padding-bottom: 0.5rem;

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }
`;

const LinkButton = styled.button`
    align-items: center;
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.link};
    cursor: pointer;
    display: flex;
    padding: 0;
    text-decoration: underline;
    text-align: left;

    &:hover:not(:disabled) {
        opacity: 0.7;
    }

    &:disabled {
        color: ${({ theme }) => theme.colors.textDisabled};
        cursor: not-allowed;
        text-decoration: none;
    }
`;
